import { useEffect, useState } from "react";
import { s } from "../style.js";
import { fetchRepos, estimateDescription, createTask } from "../api";
import type { Page, Repo } from "../types";

interface EstimatedTask {
  title: string;
  complexity: string;
  timeEstimate: string;
  repos: string[];
  summary: string;
  prompt: string;
}

const complexityColor = (level: string) => {
  switch (level?.toLowerCase()) {
    case "high": return { bg: "var(--color-accent)", ink: "var(--on-accent)" };
    case "medium": return { bg: "var(--color-accent-400)", ink: "var(--color-text)" };
    case "low": return { bg: "var(--color-neutral-300)", ink: "var(--color-text)" };
    default: return { bg: "var(--color-neutral-200)", ink: "var(--color-text)" };
  }
};

const labelStyle = s(
  "font-size:14px; letter-spacing:0.14em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:8px"
);

const buttonStyle = s(
  "all:unset; box-sizing:border-box; cursor:pointer; padding:12px 24px; font-family:var(--font-heading); font-weight:800; font-size:14px; letter-spacing:0.06em; text-transform:uppercase; text-align:center"
);

export default function EstimatePage({ onNavigate, onToast }: { onNavigate: (page: Page) => void; onToast: (message: string, type: "success" | "error") => void }) {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [description, setDescription] = useState("");
  const [selectedRepos, setSelectedRepos] = useState<string[]>([]);
  const [repoOpen, setRepoOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState<EstimatedTask[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState("");
  const [chunksUsed, setChunksUsed] = useState(0);
  const [taskCount, setTaskCount] = useState(5);

  useEffect(() => {
    fetchRepos()
      .then((data) => setRepos(data.filter((repo: Repo) => repo.active && repo.ingestionStatus === "done")))
      .catch(() => setRepos([]));
  }, []);

  const handleEstimate = async () => {
    if (!description.trim()) {
      setError("Describe what you want to build");
      return;
    }
    setLoading(true);
    setError("");
    setResults(null);
    setSelected(new Set());
    try {
      const data = await estimateDescription(description.trim(), selectedRepos, taskCount);
      setResults(data.tasks || []);
      setChunksUsed(data.relevantChunks || 0);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Estimation failed";
      setError(message);
      onToast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = (index: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    if (!results) return;
    setSelected(selected.size === results.length ? new Set() : new Set(results.map((_, index) => index)));
  };

  const repoNameToId = Object.fromEntries(repos.map((r) => [r.name, r.id]));

  const handleCreateTasks = async () => {
    if (!results || selected.size === 0) return;
    setCreating(true);
    setError("");
    try {
      const tasksToCreate = results.filter((_, i) => selected.has(i));
      for (const task of tasksToCreate) {
        const repoIds = (task.repos || []).map((name) => repoNameToId[name]).filter(Boolean);
        await createTask({
          title: task.title,
          description: task.summary,
          aiPrompt: task.prompt,
          repos: repoIds,
          status: "Backlog",
        });
      }
      onNavigate("sprint");
      onToast("Tasks created", "success");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to create tasks";
      setError(message);
      onToast(message, "error");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={s("flex:1; display:flex; flex-direction:column; min-height:0; overflow-y:auto; padding:32px")}> 
      <div style={{ ...s("border:2px solid var(--color-divider); padding:24px; margin-bottom:24px"), background: "var(--color-surface)" }}>
        <div style={labelStyle}>What do you want to build?</div>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Describe the feature, change, or problem you want to solve"
          style={{ ...s("width:100%; min-height:140px; box-sizing:border-box; resize:vertical; padding:14px; font:inherit; font-size:15px; line-height:1.5; color:var(--color-text); background:var(--color-bg); border:2px solid var(--color-divider); margin-bottom:20px"), outline: "none" }}
        />

        <div style={s("position:relative; margin-bottom:20px")}> 
          <div style={labelStyle}>Repositories (optional)</div>
          <button
            type="button"
            onClick={() => setRepoOpen((open) => !open)}
            style={s("cursor:pointer; width:100%; min-height:46px; padding:8px 14px; display:flex; flex-wrap:wrap; align-items:center; gap:6px; text-align:left; color:var(--color-text); background:var(--color-bg); border:2px solid var(--color-divider); font:inherit; box-sizing:border-box")}
          >
            {selectedRepos.length === 0 ? (
              <span style={s("color:var(--color-neutral-600); font-size:15px")}>Select repos</span>
            ) : (
              selectedRepos.map((id) => (
                <span key={id} style={s("display:inline-flex; padding:3px 10px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-200); color:var(--color-neutral-800)")}>
                  {repos.find((repo) => repo.id === id)?.name || id}
                </span>
              ))
            )}
            <span style={s("flex:1")} />
            <span style={s("font-size:11px; color:var(--color-neutral-600)")}>{repoOpen ? "▴" : "▾"}</span>
          </button>
          {repoOpen && (
            <div style={s("position:absolute; top:100%; left:0; right:0; margin-top:4px; background:var(--color-surface); border:2px solid var(--color-divider); z-index:10; max-height:220px; overflow:auto")}>
              {repos.length === 0 ? (
                <div style={s("padding:10px 14px; font-size:14px; color:var(--color-neutral-600)")}>No ingested repos</div>
              ) : repos.map((repo) => {
                const checked = selectedRepos.includes(repo.id);
                return (
                  <button
                    key={repo.id}
                    type="button"
                    onClick={() => setSelectedRepos((current) => checked ? current.filter((id) => id !== repo.id) : [...current, repo.id])}
                    style={s("all:unset; cursor:pointer; display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; box-sizing:border-box; font-size:14px; color:var(--color-text)")}
                  >
                    <span style={{ ...s("flex:none; width:16px; height:16px; border:2px solid var(--color-divider); display:flex; align-items:center; justify-content:center"), background: checked ? "var(--color-text)" : "transparent" }}>
                      {checked && <span style={s("display:block; width:8px; height:8px; background:var(--color-bg)")} />}
                    </span>
                    <span>{repo.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={s("display:flex; align-items:center; gap:12px; margin-bottom:20px")}>
          <div style={labelStyle}>Task breakdown</div>
          <div style={s("display:flex; gap:0")}>
            {[
              { label: "Fewer", value: 3 },
              { label: "Normal", value: 5 },
              { label: "More", value: 8 },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTaskCount(opt.value)}
                style={{
                  ...s("all:unset; cursor:pointer; padding:8px 16px; font-family:var(--font-heading); font-weight:800; font-size:11px; letter-spacing:0.06em; text-transform:uppercase; border:2px solid var(--color-divider)"),
                  background: taskCount === opt.value ? "var(--color-text)" : "transparent",
                  color: taskCount === opt.value ? "var(--color-bg)" : "var(--color-text)",
                  marginLeft: "-2px",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div style={s("display:flex; justify-content:flex-end")}> 
          <button
            type="button"
            onClick={handleEstimate}
            disabled={loading || !description.trim()}
            style={{ ...buttonStyle, background: loading || !description.trim() ? "var(--color-neutral-400)" : "var(--color-accent)", color: "var(--on-accent)", cursor: loading || !description.trim() ? "not-allowed" : "pointer" }}
          >
            {loading ? "Analyzing..." : "Estimate"}
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ ...s("border:2px solid var(--color-divider); padding:48px; text-align:center; margin-bottom:24px"), background: "var(--color-surface)" }}>
          <div style={s("font-family:var(--font-heading); font-weight:800; font-size:18px; color:var(--color-neutral-600); letter-spacing:0.06em")}>Analyzing codebase...</div>
          <div style={s("font-size:13px; color:var(--color-neutral-500); margin-top:8px")}>Finding relevant code and breaking work into tasks</div>
        </div>
      )}

      {error && !loading && (
        <div style={{ ...s("border:2px solid var(--color-accent); padding:16px 20px; margin-bottom:24px"), background: "var(--color-accent)" }}>
          <div style={s("font-size:14px; color:var(--on-accent)")}>{error}</div>
        </div>
      )}

      {results && !loading && (
        <div style={{ ...s("border:2px solid var(--color-divider); padding:20px 24px"), background: "var(--color-surface)" }}>
          <div style={s("display:flex; align-items:center; justify-content:space-between; gap:16px; margin-bottom:20px; flex-wrap:wrap")}> 
            <div>
              <div style={s("font-family:var(--font-heading); font-weight:800; font-size:18px; letter-spacing:-0.01em")}>Estimated tasks</div>
              <div style={s("font-size:13px; color:var(--color-neutral-600); margin-top:4px")}>{chunksUsed} code chunks used</div>
            </div>
            <label style={s("display:flex; align-items:center; gap:8px; cursor:pointer; font-family:var(--font-heading); font-weight:800; font-size:12px; letter-spacing:0.08em; text-transform:uppercase; color:var(--color-neutral-600)")}>
              <input type="checkbox" checked={results.length > 0 && selected.size === results.length} onChange={toggleAll} style={s("width:16px; height:16px; accent-color:var(--color-accent)")} />
              Select all
            </label>
          </div>

          <div style={s("display:flex; flex-direction:column; gap:16px")}> 
            {results.map((task, index) => {
              const colors = complexityColor(task.complexity);
              return (
                <div key={`${task.title}-${index}`} style={{ ...s("border:2px solid var(--color-divider); padding:20px"), background: "var(--color-bg)" }}>
                  <div style={s("display:flex; align-items:flex-start; gap:14px")}> 
                    <input type="checkbox" checked={selected.has(index)} onChange={() => toggleTask(index)} style={s("flex:none; width:18px; height:18px; margin-top:3px; accent-color:var(--color-accent)")} />
                    <div style={s("flex:1; min-width:0")}> 
                      <div style={s("display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap; margin-bottom:8px")}> 
                        <div style={s("font-family:var(--font-heading); font-weight:800; font-size:17px; color:var(--color-text)")}>{task.title}</div>
                        <span style={{ ...s("padding:4px 10px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"), background: colors.bg, color: colors.ink }}>{task.complexity}</span>
                      </div>
                      <div style={s("font-family:var(--font-heading); font-weight:800; font-size:13px; color:var(--color-neutral-600); margin-bottom:12px")}>{task.timeEstimate}</div>
                      <div style={s("display:flex; flex-wrap:wrap; gap:5px; margin-bottom:14px")}> 
                        {task.repos?.map((repo) => <span key={repo} style={s("padding:3px 8px; font-size:10px; letter-spacing:0.07em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-200); color:var(--color-neutral-800)")}>{repo}</span>)}
                      </div>
                      <div style={s("font-size:14px; line-height:1.55; color:var(--color-text); margin-bottom:16px")}>{task.summary}</div>
                      <div style={labelStyle}>Prompt for AI agent</div>
                      <pre style={s("margin:0; padding:14px; overflow:auto; white-space:pre-wrap; font-family:monospace; font-size:12px; line-height:1.5; color:var(--color-text); background:var(--color-surface); border:1px solid var(--color-divider)")}>{task.prompt}</pre>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {results.length === 0 && <div style={s("padding:24px 0; font-size:14px; color:var(--color-neutral-600)")}>No tasks returned.</div>}
          <div style={s("display:flex; justify-content:flex-end; gap:10px; margin-top:20px")}> 
            <button type="button" onClick={() => { setResults(null); setSelected(new Set()); setError(""); }} style={{ ...buttonStyle, color: "var(--color-text)", border: "2px solid var(--color-divider)" }}>Refine</button>
            <button type="button" onClick={handleCreateTasks} disabled={selected.size === 0 || creating} style={{ ...buttonStyle, background: selected.size === 0 || creating ? "var(--color-neutral-400)" : "var(--color-accent)", color: "var(--on-accent)", cursor: selected.size === 0 || creating ? "not-allowed" : "pointer" }}>{creating ? "Creating..." : `Create Tasks (${selected.size})`}</button>
          </div>
        </div>
      )}
    </div>
  );
}
